import { z } from 'zod';

/** Four primary SEND areas of need (ISP provision + APDR rows) */
export const AreaOfNeedEnum = z.enum([
  'cognitionLearning',
  'communicationInteraction',
  'semh',
  'sensoryPhysical',
]);

export const AREA_OF_NEED_LABELS = {
  cognitionLearning: 'Cognition & Learning (C&L)',
  communicationInteraction: 'Communication & Interaction (C&I)',
  semh: 'Social, Emotional & Mental Health (SEMH)',
  sensoryPhysical: 'Sensory & Physical (S&P)',
};

export const AREA_OF_NEED_SHORT = {
  cognitionLearning: 'C&L',
  communicationInteraction: 'C&I',
  semh: 'SEMH',
  sensoryPhysical: 'S&P',
};

export const ProvisionCategoryEnum = z.enum([
  'staff_support',
  'access_environment',
  'equipment_resource',
  'strategy_adjustment',
]);

export const PROVISION_CATEGORY_LABELS = {
  staff_support: 'Staff support',
  access_environment: 'Access & environment',
  equipment_resource: 'Equipment & resources',
  strategy_adjustment: 'Strategy & adjustment',
};

export const DeliveryContextEnum = z.enum([
  'in_lessons',
  'outside_lessons',
  'breaktimes',
  'transitions',
  'whole_day',
]);

export const RatioEnum = z.enum(['1:1', '1:2', '1:3', 'small_group', 'n/a']);

export const ProvisionTypeEnum = z.enum(['staff', 'equipment', 'space', 'external', 'other']);

export const ProvisionStatusEnum = z.enum(['active', 'planned', 'ended']);

export const AchievedEnum = z.enum(['Achieved', 'Partially', 'Not yet', '']);

export const ApdrInterventionSourceEnum = z.enum(['intervention', 'provision', 'manual']);

/** Row in APDR cycle "How will this be achieved?" column */
export const ApdrInterventionRowSchema = z.object({
  name: z.string(),
  frequency: z.string().optional(),
  duration: z.string().optional(),
  ratio: z.string().optional(),
  sourceType: ApdrInterventionSourceEnum.optional(),
  sourceId: z.string().optional(),
});

/** Single row in a Cycles of Support table */
export const ApdrCycleRowSchema = z.object({
  id: z.string(),
  areaOfNeed: AreaOfNeedEnum,
  areaLabel: z.string().optional(),
  assessNotes: z.string().optional(),
  smartTarget: z.string().default(''),
  interventions: z.array(ApdrInterventionRowSchema).default([]),
  outcome: z.string().optional(),
  achieved: AchievedEnum.optional(),
});

/** One term cycle within apdrSupportPlan */
export const ApdrSupportCycleSchema = z.object({
  cycleNumber: z.number().min(1).max(3),
  term: z.string().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
  assessSummary: z.string().optional(),
  rows: z.array(ApdrCycleRowSchema).default([]),
  reviewDate: z.string().optional(),
  reviewedBy: z.string().optional(),
  legacyOutcome: z.string().optional(),
  legacyNotes: z.string().optional(),
});

/** Canonical ISP APDR plan on student doc */
export const ApdrSupportPlanSchema = z.object({
  academicYear: z.string(),
  cycles: z.array(ApdrSupportCycleSchema).default([]),
});

/** studentProvisions/{id} */
export const StudentProvisionSchema = z.object({
  studentId: z.string(),
  schoolId: z.string(),
  areaOfNeed: AreaOfNeedEnum,
  category: ProvisionCategoryEnum,
  presetKey: z.string().optional(),
  name: z.string().min(1),
  description: z.string().optional(),
  deliveryContext: DeliveryContextEnum.optional(),
  subjectAreas: z.array(z.string()).default([]),
  frequency: z.string().default('Weekly'),
  duration: z.string().optional(),
  ratio: z.string().optional(),
  provisionType: ProvisionTypeEnum.default('staff'),
  staffId: z.string().optional(),
  staffRoleLabel: z.string().optional(),
  resourceCostAnnual: z.number().nonnegative().optional(),
  weeklyCost: z.number().nonnegative().optional(),
  totalAnnual: z.number().nonnegative().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  status: ProvisionStatusEnum.default('active'),
  notes: z.string().optional(),
});

/** tacMeetings/{id} — stored as "tacMeetings" for backwards compatibility; surfaced in the UI as "Multi-agency meetings" */
export const TacMeetingSchema = z.object({
  studentId: z.string(),
  schoolId: z.string(),
  meetingType: z.enum(['TAC', 'TAF', 'Other']).default('TAC'),
  date: z.string().optional(),
  attendees: z.array(z.string()).default([]),
  progress: z.string().optional(),
  areasForDevelopment: z.string().optional(),
  agreedActions: z.string().optional(),
  linkedCycleNumber: z.number().optional(),
});

export const DEFAULT_ACADEMIC_YEAR = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const startYear = month >= 8 ? year : year - 1;
  return `${startYear}/${String(startYear + 1).slice(-2)}`;
};

export const DEFAULT_TERM_CYCLES = (academicYear) => [
  { cycleNumber: 1, term: 'Autumn', rows: [] },
  { cycleNumber: 2, term: 'Spring', rows: [] },
  { cycleNumber: 3, term: 'Summer', rows: [] },
].map((c) => ({ ...c, academicYear }));

export function createEmptyApdrSupportPlan(academicYear = DEFAULT_ACADEMIC_YEAR()) {
  return {
    academicYear,
    cycles: [
      { cycleNumber: 1, term: 'Autumn', fromDate: '', toDate: '', rows: [] },
      { cycleNumber: 2, term: 'Spring', fromDate: '', toDate: '', rows: [] },
      { cycleNumber: 3, term: 'Summer', fromDate: '', toDate: '', rows: [] },
    ],
  };
}

export function migrateApdrCyclesToSupportPlan(apdrCycles = [], academicYear = DEFAULT_ACADEMIC_YEAR()) {
  const plan = createEmptyApdrSupportPlan(academicYear);
  if (!Array.isArray(apdrCycles)) return plan;

  apdrCycles.forEach((cycle) => {
    const num = Number(cycle?.cycleNumber) || 0;
    if (num < 1 || num > 3) return;
    const target = plan.cycles.find((c) => c.cycleNumber === num);
    if (!target) return;
    target.reviewDate = cycle.reviewDate || target.reviewDate || '';
    target.reviewedBy = cycle.reviewedBy || target.reviewedBy || '';
    target.legacyOutcome = cycle.outcome || '';
    target.legacyNotes = cycle.notes || '';
  });

  return plan;
}

/** Map cycle-first ISP plan to area-first PlanDoReview for EHCNA DOCX */
export function planDoReviewFromApdrSupportPlan(apdrSupportPlan) {
  const areas = ['cognitionLearning', 'communicationInteraction', 'semh', 'sensoryPhysical'];
  const result = Object.fromEntries(areas.map((a) => [a, []]));

  if (!apdrSupportPlan?.cycles?.length) return result;

  apdrSupportPlan.cycles.forEach((cycle) => {
    const byArea = {};
    (cycle.rows || []).forEach((row) => {
      const area = row.areaOfNeed;
      if (!areas.includes(area)) return;
      if (!byArea[area]) {
        byArea[area] = {
          cycleNumber: cycle.cycleNumber,
          term: cycle.term,
          focus: cycle.assessSummary || '',
          multiAgencyAdvice: '',
          smartTargets: [],
          interventions: [],
        };
      }
      if (row.smartTarget) {
        byArea[area].smartTargets.push({
          target: row.smartTarget,
          howAchieved: (row.interventions || []).map((i) => i.name).join('; '),
          achieved: row.achieved || '',
          feedback: row.outcome || '',
        });
      }
      (row.interventions || []).forEach((i) => {
        byArea[area].interventions.push({
          name: i.name,
          frequency: i.frequency,
          duration: i.duration,
          ratio: i.ratio,
        });
      });
    });
    Object.entries(byArea).forEach(([area, cycleData]) => {
      result[area].push(cycleData);
    });
  });

  return result;
}

export function newRowId() {
  return `row_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}
